import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';

import { Label } from '@comparethemarketau/manor-label';
import { Typography } from '@comparethemarketau/manor-typography';

import { connect } from 'react-redux';
import { getBreakpointFromManor } from 'ctm-components';

import { coverTypeStateMapping, excessStateMapping } from '../../filters/common/filter_store_mapping';

import { HigherExcessHighlightCard } from './higher_excess_highlight_card';
import { withAbTesting } from '../../with_ab_testing';

const useStyles = createUseStyles((theme) => ({
    excessWithTooltipContainer: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        paddingTop: theme.spacing[8],
        paddingBottom: theme.spacing[8]
    },
    showHigherExcessHighlight: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: theme.spacing[4],
        rowGap: theme.spacing[4],
        justifyContent: 'center',
        extend: ({ breakpoint }) => {
            return ['xs', 'sm'].includes(breakpoint) ? { width: '95%' } : { width: '100%' };
        }
    },
    additionalExcessMayApply: {
        marginTop: theme.spacing[4],
        fontSize: theme.fontSize.xs,
        color: theme.colors.grey700,
        textAlign: 'center',
        lineHeight: theme.lineHeight.relaxed
    }
}));

const _ExcessWithTooltip = (props) => {
    const breakpoint = getBreakpointFromManor();
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme, breakpoint });

    const { productExcess, selectedCoverTypeValue, selectedExcessValue, isHideHighExcess } = props;
    const excessAmountWithSymbol = productExcess ? '$'.concat(productExcess) : null;
    const selectedExcessValueWithSymbol = selectedExcessValue ? '$'.concat(selectedExcessValue) : null;

    const tooltipContent = {
        trackingLabel: 'Tooltip for product excess if covertype equals Comprehensive',
        iconSize: 'sm',
        placement: 'top-end',
        title: 'The excess is the amount that you may need to pay when making a claim.',
        body: 'Increasing the excess may reduce the premium. Other excesses may also apply.'
    };

    const tooltipContentForThirdParty = {
        trackingLabel: 'Tooltip for product excess if covertype equals TPFT or TPPD',
        iconSize: 'sm',
        placement: 'top-end',
        body: 'The excess is the amount that you may need to pay when making a claim. Other excesses may also apply.'
    };

    const getTooltip = function () {
        return selectedCoverTypeValue === 'COMPREHENSIVE' ? tooltipContent : tooltipContentForThirdParty;
    };

    const isShowHigherExcess = productExcess > selectedExcessValue && !isHideHighExcess;

    return (
        <div className={classes.excessWithTooltipContainer}>
            <div className={classNames(isShowHigherExcess && classes.showHigherExcessHighlight)}>
                {
                    isShowHigherExcess && <HigherExcessHighlightCard excessText={selectedExcessValueWithSymbol} />
                }
                <Label
                    text={(
                        <Typography component="span" color={isShowHigherExcess ? theme.colors.error500 : theme.colors.grey800}>
                            <Typography variant="subtitle2" display="inline">{excessAmountWithSymbol}</Typography>
                            {' Excess '}
                        </Typography>
                    )}
                    tooltip={getTooltip()}
                    removeGutters
                />
            </div>
            <Typography className={classes.additionalExcessMayApply}>Additional excesses may apply</Typography>
        </div>
    );
};

_ExcessWithTooltip.propTypes = {
    productExcess: PropTypes.number.isRequired,
    selectedCoverTypeValue: PropTypes.oneOf(['COMPREHENSIVE', 'TPFT', 'TPPD']),
    selectedExcessValue: PropTypes.number.isRequired,
    isHideHighExcess: PropTypes.bool
};

const mapStateToProps = (state) => {
    const { stagedValue: selectedCoverTypeValue } = coverTypeStateMapping(state);
    const { selectedValue: selectedExcessValue } = excessStateMapping(state);

    const isHideHighExcess = state.getIn(['page', 'isHideHighExcess']);

    return {
        selectedCoverTypeValue,
        selectedExcessValue: Number(selectedExcessValue),
        isHideHighExcess
    };
};

const ExcessWithTooltip = connect(mapStateToProps)(_ExcessWithTooltip);

export { ExcessWithTooltip, _ExcessWithTooltip };
